/**
 * AdminDiscountCodesPage — admin overzicht van ALLE kortingscodes.
 *
 * Functies:
 *  - Lijst met filter (actief/inactief), zoekfunctie
 *  - Stats: totaal / actief / gebruikt / verlopen
 *  - Per code: aanmaker, klant-koppeling, type/value, looptijd, usage, promo-URL
 *  - Toggle actief/inactief (PATCH endpoint)
 *  - Settings-knop voor admin (max % per rol, allowed packages)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Tag, RefreshCw, Loader2, Search, Filter, Copy, Power, PowerOff, Settings as SettingsIcon, X, Check,
} from 'lucide-react';

export default function AdminDiscountCodesPage() {
  const { api } = useAuth();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [showSettings, setShowSettings] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api('get', `/api/team/discount-codes${statusFilter ? `?status=${statusFilter}` : ''}`);
      setCodes(r.data.discount_codes || []);
    } catch {
      toast.error('Kortingscodes laden mislukt');
    }
    setLoading(false);
  }, [api, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const filtered = codes.filter((c) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (c.code || '').toLowerCase().includes(q) ||
      (c.gekoppelde_klant_email || '').toLowerCase().includes(q) ||
      (c.aangemaakt_door_name || '').toLowerCase().includes(q)
    );
  });

  const stats = {
    totaal: codes.length,
    actief: codes.filter((c) => c.active).length,
    gebruikt: codes.filter((c) => (c.current_usage || 0) > 0).length,
    inactief: codes.filter((c) => !c.active).length,
  };

  const toggleActive = async (code) => {
    try {
      await api('patch', `/api/team/discount-codes/${code.id}`, { active: !code.active });
      toast.success(code.active ? 'Code gedeactiveerd' : 'Code geactiveerd');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Wijzigen mislukt');
    }
  };

  const copyUrl = (url, code) => {
    navigator.clipboard.writeText(url);
    toast.success(`Promo-URL voor ${code} gekopieerd`);
  };

  return (
    <div className="min-h-screen bg-[#0f172a] text-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
          <div>
            <p className="text-[#D4A017] text-xs uppercase tracking-wider font-bold mb-1">Admin</p>
            <h1 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: "'Sora', sans-serif" }}>
              Alle kortingscodes
            </h1>
            <p className="text-white/60 text-sm mt-1">Overzicht van alle promo-codes uit de hiërarchie</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowSettings(true)} className="bg-white/5 hover:bg-white/10 border border-white/10 text-white rounded-lg px-3 py-2 text-sm flex items-center gap-1.5" data-testid="admin-discount-settings-open">
              <SettingsIcon size={14} /> Instellingen
            </button>
            <button onClick={load} className="bg-white/5 hover:bg-white/10 border border-white/10 text-white rounded-lg px-3 py-2 text-sm flex items-center gap-1.5" data-testid="admin-discount-refresh">
              <RefreshCw size={14} /> Refresh
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Totaal', value: stats.totaal, color: 'text-[#D4A017]' },
            { label: 'Actief', value: stats.actief, color: 'text-emerald-400' },
            { label: 'Gebruikt', value: stats.gebruikt, color: 'text-blue-400' },
            { label: 'Inactief', value: stats.inactief, color: 'text-white/40' },
          ].map((s) => (
            <div key={s.label} className="bg-white/5 rounded-xl p-4 border border-white/5" data-testid={`admin-discount-stat-${s.label.toLowerCase()}`}>
              <p className="text-white/50 text-xs uppercase tracking-wide font-semibold mb-1">{s.label}</p>
              <p className={`text-2xl font-bold ${s.color}`} style={{ fontFamily: "'Sora', sans-serif" }}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 mb-5 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Zoek op code, klant of aanmaker"
              className="w-full bg-white/5 border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-white/40"
              data-testid="admin-discount-search"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter size={14} className="text-white/40" />
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" data-testid="admin-discount-status-filter">
              <option value="">Alle</option>
              <option value="active">Actief</option>
              <option value="inactive">Inactief</option>
            </select>
          </div>
        </div>

        {/* Lijst */}
        {loading ? (
          <div className="text-white/40 flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Laden…</div>
        ) : filtered.length === 0 ? (
          <div className="bg-white/5 rounded-xl p-12 text-center text-white/40" data-testid="admin-discount-empty">Geen kortingscodes gevonden.</div>
        ) : (
          <div className="space-y-3">
            {filtered.map((c) => (
              <div key={c.id} className="bg-white/5 rounded-xl p-4 border border-white/10" data-testid={`admin-discount-row-${c.id}`}>
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <code className="bg-[#D4A017]/10 text-[#D4A017] px-2 py-1 rounded font-mono font-bold">{c.code}</code>
                      {c.active
                        ? <span className="text-emerald-400 text-xs flex items-center gap-1"><Check size={10} /> Actief</span>
                        : <span className="text-red-400 text-xs flex items-center gap-1"><X size={10} /> Inactief</span>}
                      <span className="text-white/40 text-xs">
                        {c.discount_type === 'percentage' ? `${c.discount_value}% korting` : `€${c.discount_value} korting`}
                      </span>
                      <span className="text-white/40 text-xs">· eerste {c.valid_first_x_months} mnd</span>
                      <span className="text-white/40 text-xs">· {c.current_usage}/{c.max_usage} gebruikt</span>
                    </div>
                    <p className="text-white/60 text-xs">
                      Aanmaker: <span className="text-white">{c.aangemaakt_door_name || c.aangemaakt_door_user_id?.slice(0, 8)}</span>
                      <span className="text-white/30 ml-2">({c.rol_aanmaker})</span>
                      {c.gekoppelde_klant_email && <span className="ml-3">→ Klant: <span className="text-white">{c.gekoppelde_klant_email}</span></span>}
                    </p>
                    <p className="text-white/40 text-xs font-mono mt-1 truncate">{c.promo_url}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button onClick={() => copyUrl(c.promo_url, c.code)} className="bg-white/5 hover:bg-white/10 border border-white/10 text-white rounded-lg px-3 py-1.5 text-xs flex items-center gap-1.5" data-testid={`admin-copy-url-${c.id}`}>
                      <Copy size={12} /> URL
                    </button>
                    <button onClick={() => toggleActive(c)} className={`rounded-lg px-3 py-1.5 text-xs flex items-center gap-1.5 ${c.active ? 'bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30' : 'bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'}`} data-testid={`admin-toggle-${c.id}`}>
                      {c.active ? (<><PowerOff size={12} /> Deactiveer</>) : (<><Power size={12} /> Activeer</>)}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {showSettings && <SettingsModal api={api} onClose={() => setShowSettings(false)} />}
      </div>
    </div>
  );
}


function SettingsModal({ api, onClose }) {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api('get', '/api/admin/discount-settings').then((r) => setSettings(r.data.settings)).catch(() => toast.error('Settings laden mislukt'));
  }, [api]);

  const save = async () => {
    setSaving(true);
    try {
      await api('patch', '/api/admin/discount-settings', { settings });
      toast.success('Instellingen opgeslagen');
      onClose();
    } catch {
      toast.error('Opslaan mislukt');
    }
    setSaving(false);
  };

  if (!settings) {
    return (
      <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-[#0f172a] border border-white/10 rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
          <Loader2 className="animate-spin" />
        </div>
      </div>
    );
  }

  const upd = (k, v) => setSettings((s) => ({ ...s, [k]: v }));

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-[#0f172a] border border-white/10 rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="admin-discount-settings-modal">
        <div className="flex items-start justify-between mb-4">
          <h2 className="text-xl font-bold" style={{ fontFamily: "'Sora', sans-serif" }}>Korting-instellingen</h2>
          <button onClick={onClose}><X size={18} className="text-white/40 hover:text-white" /></button>
        </div>

        <div className="space-y-4 text-sm">
          <p className="text-xs uppercase tracking-wide text-white/50 font-bold">Mag aanmaken</p>
          {['agent', 'teamleider', 'partner', 'projectmanager'].map((role) => (
            <label key={role} className="flex items-center justify-between gap-2 cursor-pointer">
              <span className="capitalize text-white/80">{role}</span>
              <input
                type="checkbox"
                checked={settings[`${role}_can_create`]}
                onChange={(e) => upd(`${role}_can_create`, e.target.checked)}
                className="w-4 h-4 accent-[#D4A017]"
              />
            </label>
          ))}
          <p className="text-xs uppercase tracking-wide text-white/50 font-bold mt-3">Max korting % per rol</p>
          {['agent', 'teamleider', 'partner', 'projectmanager'].map((role) => (
            <label key={role} className="flex items-center justify-between gap-2">
              <span className="capitalize text-white/80">{role}</span>
              <input
                type="number"
                min={0} max={100}
                value={settings[`max_discount_percentage_${role}`]}
                onChange={(e) => upd(`max_discount_percentage_${role}`, Number(e.target.value))}
                className="bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white w-20 text-right"
              />
            </label>
          ))}

          <label className="flex items-center justify-between gap-2 mt-2">
            <span className="text-white/80">Max looptijd (maanden)</span>
            <input
              type="number" min={1} max={24}
              value={settings.max_first_x_months}
              onChange={(e) => upd('max_first_x_months', Number(e.target.value))}
              className="bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white w-20 text-right"
            />
          </label>
        </div>

        <button onClick={save} disabled={saving} className="w-full mt-5 bg-[#D4A017] hover:bg-[#B8860B] disabled:opacity-50 text-[#0f172a] font-bold py-2.5 rounded-xl flex items-center justify-center gap-2" data-testid="admin-discount-settings-save">
          {saving ? <><Loader2 size={14} className="animate-spin" /> Opslaan…</> : <><Check size={14} /> Opslaan</>}
        </button>
      </div>
    </div>
  );
}
